import type { IntakeSpec, MechanicalGearDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';

function getDetails(intake: IntakeSpec): MechanicalGearDetails {
  return intake.details as MechanicalGearDetails;
}

function gearSnippet(d: MechanicalGearDetails): string {
  const m = d.module;
  const t = d.toothCount;
  const w = d.faceWidth;
  const pa = d.pressureAngle;
  switch (d.gearType) {
    case 'spur':
      return `spur_gear(mod=${m}, teeth=${t}, thickness=${w}, pressure_angle=${pa}, anchor=BOT);`;
    case 'helical':
      // 20° helix is the conventional default for parallel-shaft helical gears.
      return `spur_gear(mod=${m}, teeth=${t}, thickness=${w}, pressure_angle=${pa}, helical=20, anchor=BOT);`;
    case 'bevel':
      // Self-mating 90° miter gear; mate_teeth equals teeth.
      return `bevel_gear(mod=${m}, teeth=${t}, mate_teeth=${t}, face_width=${w}, pressure_angle=${pa}, anchor="pitchbase");`;
    case 'worm':
      return `worm(mod=${m}, d=${m * t}, l=${w}, starts=1, pressure_angle=${pa}, anchor=CENTER);`;
    case 'rack':
      return `rack(mod=${m}, teeth=${t}, thickness=${w}, pressure_angle=${pa}, anchor=CENTER);`;
  }
}

function gearBoundingBox(d: MechanicalGearDetails): { w: number; d: number; h: number } {
  // Outer (addendum) diameter = module × (teeth + 2). Used only for the
  // cuboid fallback if BOSL2 is unavailable; real geometry sets its own bounds.
  if (d.gearType === 'rack') {
    return { w: d.toothCount * d.module * Math.PI, d: d.module * 4, h: d.faceWidth };
  }
  if (d.gearType === 'worm') {
    return { w: d.faceWidth, d: d.module * d.toothCount, h: d.module * d.toothCount };
  }
  const od = (d.toothCount + 2) * d.module;
  return { w: od, d: od, h: d.faceWidth };
}

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);
  return [
    {
      id: 'gear',
      name: '歯車',
      kind: 'custom',
      materialId: 'mat-primary',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      tags: ['gear', d.gearType],
      paramRefs: [],
      openscadSnippet: gearSnippet(d),
      boundingBox: gearBoundingBox(d),
    },
  ];
}

export function joints(_intake: IntakeSpec): Joint[] {
  return [];
}

export function features(intake: IntakeSpec): Feature[] {
  if (intake.qualityLevel === 'draft' || intake.qualityLevel === 'standard') return [];

  const d = getDetails(intake);
  const out: Feature[] = [];

  if (d.boreDiameter > 0) {
    out.push({
      id: 'bore_hole',
      partId: 'gear',
      kind: 'dowel_hole',
      diameter: d.boreDiameter,
      depth: d.faceWidth,
      axis: 'z',
      through: true,
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });
  }

  if (d.hubStyle === 'keyed') {
    // keyed hub: keyway feature kind is B2 rest, deferred. Treat as set_screw for V1.
    //
    out.push({
      id: 'set_screw_hole',
      partId: 'gear',
      kind: 'screw_hole',
      size: 'M3',
      length: 8,
      head: 'socket',
      axis: 'x',
      thread: true,
      teardrop: false,
      position: [d.boreDiameter / 2 + 2, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });
  } else if (d.hubStyle === 'set_screw') {
    out.push({
      id: 'set_screw_hole',
      partId: 'gear',
      kind: 'screw_hole',
      size: 'M3',
      length: 8,
      head: 'socket',
      axis: 'x',
      thread: true,
      teardrop: false,
      position: [d.boreDiameter / 2 + 2, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });
  }

  return out;
}
